import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Input, Button, Table, Select, Tooltip, Tag, Avatar, Typography, Flex, Card } from "antd";
import { useDispatch } from "react-redux";
import axios from "axios";
import moment from "moment";
import Layout from "../../components/layout";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  FileExcelOutlined, FilePdfOutlined, ClearOutlined,
  ReloadOutlined, SearchOutlined, FilterOutlined,
  FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ClockCircleOutlined, SyncOutlined,
  EnvironmentOutlined, MedicineBoxOutlined
} from "@ant-design/icons";

const { Title, Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Status config ──────────────────────────────────────────── */
const STATUS_CFG = {
  pending:     { color: "#f59e0b", bg: "#fef3c7", label: "Pending",   icon: <ClockCircleOutlined /> },
  approved:    { color: "#3b82f6", bg: "#dbeafe", label: "Approved",  icon: <CheckCircleOutlined /> },
  "follow-up": { color: "#8b5cf6", bg: "#ede9fe", label: "Follow-up", icon: <SyncOutlined /> },
  completed:   { color: "#10b981", bg: "#d1fae5", label: "Completed", icon: <CheckCircleOutlined /> },
  rejected:    { color: "#ef4444", bg: "#fee2e2", label: "Rejected",  icon: <CloseCircleOutlined /> },
  canceled:    { color: "#94a3b8", bg: "#f1f5f9", label: "Canceled",  icon: <CloseCircleOutlined /> },
};
const getStatusCfg = (s) => STATUS_CFG[s?.toLowerCase()] || { color: MUTED, bg: SURFACE, label: s, icon: null };

/* ─── Style injection ────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("appt-report-styles")) return;
  const el = document.createElement("style");
  el.id = "appt-report-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    /* ── Root & Layout ── */
    .ar-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }
    .ar-wrap { max-width: 1400px; margin: 0 auto; padding: 32px 24px 64px; }

    /* ── Hero ── */
    .ar-hero {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      border-radius: 24px; padding: 36px 44px;
      position: relative; overflow: hidden; margin-bottom: 28px;
    }
    .ar-hero::before {
      content: ''; position: absolute; top: -80px; right: -80px;
      width: 300px; height: 300px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      filter: blur(32px); pointer-events: none;
    }
    .ar-hero::after {
      content: ''; position: absolute; bottom: -60px; left: -60px;
      width: 220px; height: 220px; border-radius: 50%;
      background: radial-gradient(circle, ${EMERALD}33 0%, transparent 70%);
      filter: blur(28px); pointer-events: none;
    }

    .ar-eyebrow {
      display: inline-block; padding: 4px 12px; border-radius: 20px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
      font-family: 'Sora', sans-serif; margin-bottom: 12px;
    }

    /* ── Controls Panel ── */
    .ar-controls {
      background: ${WHITE}; border-radius: 20px;
      box-shadow: 0 2px 12px rgba(0,0,0,.05), 0 0 0 1px #f1f5f9;
      padding: 20px 24px; margin-bottom: 20px;
    }

    /* ── Input / Select focus states ── */
    .ar-input .ant-input-affix-wrapper {
      border-radius: 12px !important; height: 42px !important;
      border: 1.5px solid #e2e8f0 !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .ar-input .ant-input-affix-wrapper:hover,
    .ar-input .ant-input-affix-wrapper-focused {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    .ar-select .ant-select-selector {
      border-radius: 12px !important; height: 42px !important;
      border: 1.5px solid #e2e8f0 !important;
      display: flex !important; align-items: center !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .ar-select.ant-select-focused .ant-select-selector,
    .ar-select .ant-select-selector:hover {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* ── Buttons ── */
    .ar-btn-excel {
      background: #16a34a !important; border: none !important;
      border-radius: 10px !important; height: 42px !important;
      font-weight: 600 !important; color: ${WHITE} !important;
      transition: transform .2s, box-shadow .2s !important;
    }
    .ar-btn-excel:hover { transform: translateY(-1px) !important; box-shadow: 0 6px 16px rgba(22,163,74,.35) !important; }

    .ar-btn-pdf {
      background: #dc2626 !important; border: none !important;
      border-radius: 10px !important; height: 42px !important;
      font-weight: 600 !important; color: ${WHITE} !important;
      transition: transform .2s, box-shadow .2s !important;
    }
    .ar-btn-pdf:hover { transform: translateY(-1px) !important; box-shadow: 0 6px 16px rgba(220,38,38,.35) !important; }

    .ar-btn-ghost {
      border-radius: 10px !important; height: 42px !important;
      border: 1.5px solid #e2e8f0 !important;
      font-weight: 600 !important; color: ${SLATE_MID} !important;
      transition: border-color .2s, color .2s !important;
    }
    .ar-btn-ghost:hover { border-color: ${TEAL} !important; color: ${EMERALD} !important; }

    /* ── Table Card ── */
    .ar-table-card {
      border-radius: 20px !important;
      border: 1px solid #f1f5f9 !important;
      box-shadow: 0 2px 12px rgba(0,0,0,.05) !important;
      overflow: hidden !important;
    }

    /* Card header */
    .ar-table-card .ant-card-head {
      background: ${WHITE} !important;
      border-bottom: 1px solid #f1f5f9 !important;
      padding: 0 24px !important;
      min-height: 58px !important;
    }
    .ar-table-card .ant-card-head-wrapper {
      height: 58px !important;
      align-items: center !important;
    }
    .ar-table-card .ant-card-head-title {
      font-family: 'Sora', sans-serif !important;
      font-size: 15px !important;
      color: ${SLATE} !important;
      font-weight: 700 !important;
      padding: 0 !important;
    }
    .ar-table-card .ant-card-extra { padding: 0 !important; }

    /* Card body — zero padding so table bleeds edge-to-edge */
    .ar-table-card .ant-card-body {
      padding: 0 !important;
      display: flex !important;
      flex-direction: column !important;
    }

    /* ── Table internals ── */
    .ar-table-card .ant-table-wrapper {
      flex: 1 !important;
    }

    /* Thead */
    .ar-table-card .ant-table-thead > tr > th {
      background: #f8fafc !important;
      color: ${MUTED} !important;
      font-size: 11px !important;
      font-weight: 700 !important;
      letter-spacing: .9px !important;
      text-transform: uppercase !important;
      border-bottom: 1px solid #eef2f7 !important;
      padding: 12px 16px !important;
      white-space: nowrap !important;
    }
    /* Remove double border between header and sort icon area */
    .ar-table-card .ant-table-thead > tr > th::before { display: none !important; }

    /* Tbody rows */
    .ar-table-card .ant-table-tbody > tr > td {
      border-bottom: 1px solid #f1f5f9 !important;
      padding: 13px 16px !important;
      vertical-align: middle !important;
    }
    .ar-table-card .ant-table-tbody > tr:last-child > td {
      border-bottom: none !important;
    }
    .ar-table-card .ant-table-tbody > tr:hover > td {
      background: #f0fdfb !important;
    }

    /* Sorter arrows color */
    .ar-table-card .ant-table-column-sorter-up.active,
    .ar-table-card .ant-table-column-sorter-down.active { color: ${EMERALD} !important; }

    /* Filter icon color */
    .ar-table-card .ant-table-filter-trigger.active { color: ${EMERALD} !important; }

    /* ── Pagination ── */
    .ar-table-card .ant-pagination {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      flex-wrap: wrap !important;
      gap: 8px !important;
      padding: 14px 24px !important;
      margin: 0 !important;
      border-top: 1px solid #f1f5f9 !important;
      background: ${WHITE} !important;
    }
    .ar-table-card .ant-pagination-total-text {
      font-size: 12px !important;
      color: ${MUTED} !important;
      font-weight: 500 !important;
      margin-right: auto !important;
    }
    .ar-table-card .ant-pagination-item {
      border-radius: 8px !important;
      border: 1.5px solid #e2e8f0 !important;
      font-weight: 600 !important;
      min-width: 34px !important;
      height: 34px !important;
      line-height: 31px !important;
      font-size: 13px !important;
      transition: all .18s !important;
    }
    .ar-table-card .ant-pagination-item:hover {
      border-color: ${TEAL} !important;
      color: ${EMERALD} !important;
    }
    .ar-table-card .ant-pagination-item a { color: inherit !important; }
    .ar-table-card .ant-pagination-item-active {
      background: ${EMERALD} !important;
      border-color: ${EMERALD} !important;
    }
    .ar-table-card .ant-pagination-item-active a { color: ${WHITE} !important; }

    .ar-table-card .ant-pagination-prev .ant-pagination-item-link,
    .ar-table-card .ant-pagination-next .ant-pagination-item-link {
      border-radius: 8px !important;
      border: 1.5px solid #e2e8f0 !important;
      height: 34px !important;
      width: 34px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 12px !important;
      transition: all .18s !important;
    }
    .ar-table-card .ant-pagination-prev:hover .ant-pagination-item-link,
    .ar-table-card .ant-pagination-next:hover .ant-pagination-item-link {
      border-color: ${TEAL} !important;
      color: ${EMERALD} !important;
    }

    /* Size-changer & quick-jumper */
    .ar-table-card .ant-pagination-options {
      display: flex !important;
      align-items: center !important;
      gap: 8px !important;
    }
    .ar-table-card .ant-select-selector {
      border-radius: 8px !important;
      height: 34px !important;
      border: 1.5px solid #e2e8f0 !important;
      font-size: 12px !important;
    }
    .ar-table-card .ant-pagination-options-quick-jumper input {
      border-radius: 8px !important;
      height: 34px !important;
      border: 1.5px solid #e2e8f0 !important;
      font-size: 12px !important;
      width: 52px !important;
      text-align: center !important;
    }
    .ar-table-card .ant-pagination-options-quick-jumper input:focus {
      border-color: ${TEAL} !important;
      box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    /* ── Empty state ── */
    .ar-table-card .ant-empty { padding: 48px 24px !important; }
    .ar-table-card .ant-empty-description { color: ${MUTED} !important; font-size: 13px !important; }

    /* ── Summary pills ── */
    .ar-summary { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
    .ar-summary-pill {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 5px 13px; border-radius: 20px;
      font-size: 12px; font-weight: 600;
    }

    /* ── Comment cell ── */
    .ar-comment { cursor: pointer; }
    .ar-comment-by { font-size: 10px; color: ${MUTED}; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 2px; }
    .ar-comment-text { font-size: 12px; color: ${SLATE_MID}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 150px; }

    /* ── Action info cell ── */
    .ar-action-by  { font-size: 12px; color: ${SLATE_MID}; }
    .ar-action-time { font-size: 11px; color: ${MUTED}; margin-top: 3px; }

    /* ── Animations ── */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .ar-fade   { animation: fadeUp .45s ease both; }
    .ar-fade-1 { animation-delay: .05s; }
    .ar-fade-2 { animation-delay: .12s; }
    .ar-fade-3 { animation-delay: .20s; }

    /* ── Responsive tweaks ── */
    @media (max-width: 640px) {
      .ar-hero { padding: 28px 24px; }
      .ar-wrap { padding: 20px 16px 48px; }
      .ar-table-card .ant-pagination {
        justify-content: center !important;
      }
      .ar-table-card .ant-pagination-total-text { width: 100%; text-align: center; margin-right: 0 !important; }
    }
  `;
  document.head.appendChild(el);
};

/* ─── Component ──────────────────────────────────────────────── */
const AppointmentReport = () => {
  const dispatch = useDispatch();
  const [appointments, setAppointments] = useState([]);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  useEffect(() => { injectStyles(); }, []);

  const exportToCSV = () => {
    const exportData = filteredData.map((a) => ({
      Patient: a.userInfo?.name || "N/A",
      "Appointment Service": a.doctorInfo?.service || "N/A",
      "Doctor Name": `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`,
      "Clinic Day": a.doctorInfo?.clinicDay?.join(", ") || "N/A",
      "Scheduled Date": a.schedule_date ? moment(a.schedule_date).format("DD-MM-YYYY") : "Not set",
      Status: a.status,
      Comments: a.cancelComment || a.comment || "No comments",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Appointments");
    const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "appointments.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Appointment Report", 14, 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${moment().format("MMM DD, YYYY HH:mm")}`, 14, 26);
    autoTable(doc, {
      startY: 32,
      head: [["Patient", "Service", "Doctor", "Clinic Day", "Date", "Status", "Comments"]],
      body: filteredData.map((a) => [
        a.userInfo?.name || "N/A",
        a.doctorInfo?.service || "N/A",
        `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`,
        a.doctorInfo?.clinicDay?.join(", ") || "N/A",
        a.schedule_date ? moment(a.schedule_date).format("DD-MM-YYYY") : "Not set",
        a.status,
        a.cancelComment || a.comment || "No comments",
      ]),
      headStyles: { fillColor: [13, 148, 136], fontSize: 8, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 25 }, 2: { cellWidth: 25 }, 6: { cellWidth: 30 } },
    });
    doc.save("appointments.pdf");
  };

  const getAppointmentData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const res = await axios.get("/api/admin/get-all-appointments", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      if (res.data.success) setAppointments(res.data.data);
    } catch (err) { dispatch(hideLoading()); console.error(err); }
  }, [dispatch]);

  useEffect(() => { getAppointmentData(); }, [getAppointmentData]);

  const handleChange = (_, filters, sorter) => { setFilteredInfo(filters); setSortedInfo(sorter); };
  const clearFilters = () => setFilteredInfo({});
  const clearAll     = () => { setFilteredInfo({}); setSortedInfo({}); setSearchTerm(""); setStatusFilter("All"); };

  const filteredData = appointments.filter((a) => {
    const matchesSearch = a.userInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = filteredData.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  /* ── Columns (balanced fixed widths that sum to 1080px) ── */
  const columns = [
    {
      title: "Patient",
      key: "patient",
      width: 160,
      fixed: "left",
      sorter: (a, b) => (a.userInfo?.name || "").localeCompare(b.userInfo?.name || ""),
      sortOrder: sortedInfo.columnKey === "patient" ? sortedInfo.order : null,
      render: (_, r) => (
        <Flex align="center" gap={8}>
          <Avatar
            size={32}
            style={{
              background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`,
              fontSize: 13, flexShrink: 0, fontWeight: 700,
            }}
          >
            {r.userInfo?.name?.[0]?.toUpperCase() || "?"}
          </Avatar>
          <Text style={{ fontSize: 13, color: SLATE, fontWeight: 500, lineHeight: 1.3 }}>
            {r.userInfo?.name || "N/A"}
          </Text>
        </Flex>
      ),
    },
    {
      title: "Service",
      key: "service",
      width: 145,
      ellipsis: true,
      filters: [...new Set(appointments.map((a) => a.doctorInfo?.service).filter(Boolean))].map((s) => ({ text: s, value: s })),
      filteredValue: filteredInfo.service || null,
      onFilter: (value, record) => record.doctorInfo?.service === value,
      sorter: (a, b) => (a.doctorInfo?.service || "").localeCompare(b.doctorInfo?.service || ""),
      sortOrder: sortedInfo.columnKey === "service" ? sortedInfo.order : null,
      render: (_, r) => (
        <Flex align="center" gap={6}>
          <MedicineBoxOutlined style={{ color: EMERALD, fontSize: 12, flexShrink: 0 }} />
          <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.doctorInfo?.service || "—"}</Text>
        </Flex>
      ),
    },
    {
      title: "Doctor",
      key: "doctorName",
      width: 175,
      ellipsis: true,
      sorter: (a, b) =>
        `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`.localeCompare(
          `${b.doctorInfo?.firstName || ""} ${b.doctorInfo?.lastName || ""}`
        ),
      sortOrder: sortedInfo.columnKey === "doctorName" ? sortedInfo.order : null,
      render: (_, r) => (
        <Flex align="center" gap={8}>
          <Avatar
            size={30}
            style={{ background: "#e0e7ff", color: "#6366f1", fontSize: 11, flexShrink: 0, fontWeight: 700 }}
          >
            {r.doctorInfo?.firstName?.[0]?.toUpperCase()}
          </Avatar>
          <Text style={{ fontSize: 13, color: SLATE_MID }}>
            Dr. {r.doctorInfo?.firstName} {r.doctorInfo?.lastName}
          </Text>
        </Flex>
      ),
    },
    {
      title: "Location",
      key: "location",
      width: 130,
      ellipsis: true,
      responsive: ["md"],
      filters: [...new Set(appointments.map((a) => a.doctorInfo?.location).filter(Boolean))].map((s) => ({ text: s, value: s })),
      filteredValue: filteredInfo.location || null,
      onFilter: (value, record) => record.doctorInfo?.location === value,
      sorter: (a, b) => (a.doctorInfo?.location || "").localeCompare(b.doctorInfo?.location || ""),
      sortOrder: sortedInfo.columnKey === "location" ? sortedInfo.order : null,
      render: (_, r) => (
        <Flex align="center" gap={5}>
          <EnvironmentOutlined style={{ color: MUTED, fontSize: 11, flexShrink: 0 }} />
          <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.doctorInfo?.location || "—"}</Text>
        </Flex>
      ),
    },
    {
      title: "Scheduled Date",
      key: "schedule_date",
      width: 135,
      sorter: (a, b) => moment(a.schedule_date || 0).unix() - moment(b.schedule_date || 0).unix(),
      sortOrder: sortedInfo.columnKey === "schedule_date" ? sortedInfo.order : null,
      render: (_, r) => {
        const hasDate = (r.status === "approved" || r.status === "follow-up") && r.schedule_date;
        return hasDate
          ? <Text style={{ fontSize: 13, color: SLATE_MID, fontWeight: 500 }}>{moment(r.schedule_date).format("MMM D, YYYY")}</Text>
          : <Text style={{ fontSize: 13, color: MUTED }}>—</Text>;
      },
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_, r) => {
        const cfg = getStatusCfg(r.status);
        return (
          <Tag
            style={{
              background: cfg.bg, color: cfg.color,
              border: "none", borderRadius: 20,
              fontWeight: 700, fontSize: 11,
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 10px", lineHeight: 1.6,
            }}
          >
            {cfg.icon}&nbsp;{cfg.label}
          </Tag>
        );
      },
    },
    {
      title: "Comments",
      key: "comments",
      width: 170,
      render: (_, r) => {
        const comment = r.cancelComment || r.comment;
        const by      = r.canceledByName || r.approvedByName || "System";
        if (!comment) return <Text style={{ color: MUTED }}>—</Text>;
        return (
          <Tooltip
            title={
              <div>
                <div><strong>By:</strong> {by}</div>
                <div><strong>Comment:</strong> {comment}</div>
              </div>
            }
          >
            <div className="ar-comment">
              <div className="ar-comment-by">{by}</div>
              <div className="ar-comment-text">{comment}</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Action Info",
      key: "actionsInfo",
      width: 200,
      render: (_, r) => {
        if (r.status === "approved" || r.status === "follow-up") {
          return (
            <div>
              <div className="ar-action-by">
                <CheckCircleOutlined style={{ color: "#10b981", marginRight: 5 }} />
                Approved by <strong>{r.approvedByName || "N/A"}</strong>
              </div>
              <div className="ar-action-time">
                {r.approvedAt ? moment(r.approvedAt).format("MMM DD, YYYY · HH:mm") : "—"}
              </div>
            </div>
          );
        }
        if (r.status === "canceled") {
          return (
            <div>
              <div className="ar-action-by">
                <CloseCircleOutlined style={{ color: "#ef4444", marginRight: 5 }} />
                Canceled by <strong>{r.canceledByName || "N/A"}</strong>
              </div>
              <div className="ar-action-time">
                {r.canceledAt ? moment(r.canceledAt).format("MMM DD, YYYY · HH:mm") : "—"}
              </div>
            </div>
          );
        }
        return <Text style={{ color: MUTED }}>—</Text>;
      },
    },
  ];

  return (
    <Layout>
      <div className="ar-root">
        <div className="ar-wrap">

          {/* ── Hero ── */}
          <div className="ar-hero ar-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="ar-eyebrow">
                <FileTextOutlined style={{ marginRight: 5 }} />Reports
              </div>
              <Title
                level={2}
                style={{
                  margin: "6px 0 4px",
                  fontFamily: "'Sora', sans-serif",
                  color: WHITE, fontWeight: 800,
                }}
              >
                Appointment Reports
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Filter, search, and export appointment data across all statuses
              </Text>
            </div>
          </div>

          {/* ── Controls ── */}
          <div className="ar-controls ar-fade ar-fade-1">
            <Row gutter={[14, 14]} align="middle">
              <Col xs={24} sm={12} md={7}>
                <div className="ar-input">
                  <Input
                    prefix={<SearchOutlined style={{ color: MUTED }} />}
                    placeholder="Search by patient name…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    allowClear
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Select
                  className="ar-select"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: "100%" }}
                  suffixIcon={<FilterOutlined style={{ color: MUTED }} />}
                  options={[
                    { value: "All",       label: "All Statuses" },
                    { value: "pending",   label: "Pending" },
                    { value: "approved",  label: "Approved" },
                    { value: "rejected",  label: "Rejected" },
                    { value: "follow-up", label: "Follow-up" },
                    { value: "completed", label: "Completed" },
                    { value: "canceled",  label: "Canceled" },
                  ]}
                />
              </Col>
              <Col xs={24} md={12}>
                <Flex gap={8} justify="flex-end" wrap="wrap">
                  <Button className="ar-btn-excel" icon={<FileExcelOutlined />} onClick={exportToCSV}>Export Excel</Button>
                  <Button className="ar-btn-pdf"   icon={<FilePdfOutlined />}   onClick={exportToPDF}>Export PDF</Button>
                  <Button className="ar-btn-ghost" icon={<ClearOutlined />}     onClick={clearFilters}>Clear Filters</Button>
                  <Button className="ar-btn-ghost" icon={<ReloadOutlined />}    onClick={clearAll}>Reset All</Button>
                </Flex>
              </Col>
            </Row>
          </div>

          {/* ── Status summary pills ── */}
          {filteredData.length > 0 && (
            <div className="ar-summary ar-fade ar-fade-2">
              {Object.entries(statusCounts).map(([status, count]) => {
                const cfg = getStatusCfg(status);
                return (
                  <span key={status} className="ar-summary-pill" style={{ background: cfg.bg, color: cfg.color }}>
                    {cfg.icon}&nbsp;{cfg.label}: <strong style={{ marginLeft: 2 }}>{count}</strong>
                  </span>
                );
              })}
            </div>
          )}

          {/* ── Table ── */}
          <div className="ar-fade ar-fade-3">
            <Card
              className="ar-table-card"
              title="All Appointments"
              extra={
                <Tag
                  style={{
                    background: TEAL_LIGHT, color: EMERALD,
                    border: "none", borderRadius: 20,
                    fontWeight: 700, fontSize: 12,
                    padding: "2px 12px",
                  }}
                >
                  {filteredData.length} records
                </Tag>
              }
            >
              <Table
                columns={columns}
                dataSource={filteredData.map((item) => ({ ...item, key: item._id }))}
                onChange={handleChange}
                scroll={{ x: 1235 }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ["10", "20", "50"],
                  showTotal: (total, range) =>
                    `Showing ${range[0]}–${range[1]} of ${total} appointments`,
                }}
                size="middle"
                tableLayout="fixed"
              />
            </Card>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default AppointmentReport;